import { createSlice } from '@reduxjs/toolkit';
import {
  fetchEligibleChatUsers,
  fetchConversations,
  getOrCreateConversation,
  fetchUnreadCount
} from './chatAction';
import { decrypt } from '../../utils/encryption';

const initialState = {
  messages: {},              // { [conversationId]: [msg, msg, ...] }
  conversations: [],
  selectedConversation: null,
  loading: false,
  error: null,
  eligibleUsers: [],
  onlineUsers: [], // Array of user IDs
  unreadCount: 0,  // Total unread messages
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    addMessageToConversation: (state, action) => {
      const { conversationId, message } = action.payload;
      if (!state.messages[conversationId]) {
        state.messages[conversationId] = [];
      }
      // Replace temp message with real one (avoid duplicates)
      const existingIndex = state.messages[conversationId].findIndex(
        m => {
          // If message has tempId, match against that
          if (message.tempId && m.tempId === message.tempId) return true;
          // If message has server id, match against that
          if (message.id && m.id === message.id) return true;
          return false;
        }
      );

      if (existingIndex > -1) {
        state.messages[conversationId][existingIndex] = message;
      } else {
        state.messages[conversationId].push(message);
      }
    },

    clearChatError: (state) => {
      state.error = null;
    },
    setOnlineUsers: (state, action) => {
      state.onlineUsers = (action.payload || []).map(id => String(id));
    },
    updateOnlineStatus: (state, action) => {
      const { userId, status } = action.payload;
      const sUserId = String(userId);
      if (status === 'online') {
        if (!state.onlineUsers.includes(sUserId)) {
          state.onlineUsers.push(sUserId);
        }
      } else {
        state.onlineUsers = state.onlineUsers.filter(id => String(id) !== sUserId);
      }
    },
    incrementUnreadCount: (state) => {
      state.unreadCount += 1;
    },
    resetUnreadCount: (state) => {
      state.unreadCount = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEligibleChatUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEligibleChatUsers.fulfilled, (state, action) => {
        state.loading = false;
        console.log("Fetched eligible chat users:", action);
        state.eligibleUsers = action.payload;
      })
      .addCase(fetchEligibleChatUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchConversations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.loading = false;
        state.conversations = (action.payload || []).map(conv => ({
          ...conv,
          lastMessage: conv.lastMessage ? decrypt(conv.lastMessage) : conv.lastMessage
        }));
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // chatSlice.js - getOrCreateConversation.fulfilled case
      .addCase(getOrCreateConversation.fulfilled, (state, action) => {
        // Now payload is { conversationId, messages: [...] }
        const { conversationId, messages: msgs } = action.payload;

        if (!conversationId) return;

        // Store messages (decrypted)
        state.messages[conversationId] = (msgs || []).map(msg => {
          const decryptedText = decrypt(msg.message || msg.text);
          return {
            ...msg,
            message: decryptedText,
            text: decryptedText
          };
        });

        // Add conversation if needed
        const existingIndex = state.conversations.findIndex(
          (c) => c.id === conversationId
        );
        if (existingIndex === -1) {
          // You might not have partner details here, but ID is critical
          state.conversations.unshift({ id: conversationId });
        }
      })
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload;
      });
  },
});

export const { addMessageToConversation, clearChatError, updateOnlineStatus, setOnlineUsers, incrementUnreadCount, resetUnreadCount } = chatSlice.actions;
export default chatSlice.reducer;
